// ─── Device & UI State Enums ─────────────────────────────────────────────────

export type BorderSides = 'all' | 'bottom' | 'top' | 'none'

export enum AccountPageDevice {
  MOBILE = 'MOBILE',
  DESKTOP = 'DESKTOP'
}

export enum AccountPageRealEstate {
  STORE = 'STORE',
  DASHBOARD = 'DASHBOARD'
}

export enum AccountPageExpandedSection {
  LAYOUT = 'LAYOUT',
  NAVIGATION = 'NAVIGATION',
  PROFILE = 'PROFILE',
  ORDERS = 'ORDERS',
  REWARDS = 'REWARDS',
  NONE = 'NONE'
}

export enum AccountActivePage {
  PROFILE = 'PROFILE',
  ORDERS = 'ORDERS',
}

export enum AccountPageFocusedVia {
  CANVAS = 'CANVAS',
  PANEL = 'PANEL',
  NONE = 'NONE'
}

// ─── Layout ──────────────────────────────────────────────────────────────────

export type AccountPageLayoutConfig = {
  background_color: string
  page_radius: string
  side_margin: string
}

// ─── Navigation ──────────────────────────────────────────────────────────────

export type QuickLinkItem = {
  label: string
  url: string
  enabled: boolean
}

export type AccountPageNavConfig = {
  background_color: string
  primary_color: string
  text_color: string
  tab_radius: string
  quick_links: QuickLinkItem[]
}

// ─── Profile Page ────────────────────────────────────────────────────────────

export type AccountPageProfileDetailsConfig = {
  background_color: string
  text_color: string
  border_color: string
  card_radius: string
  card_border: string
  sides: BorderSides
}

export type AccountPageProfileButtonConfig = {
  background_color: string
  text_color: string
  border_radius: string
  cta_label: string
}

export type AccountPageSavedAddressesConfig = {
  header_color: string
  background_color: string
  border_color: string
  text_color: string
  card_radius: string
  card_border: string
  sides: BorderSides
}

export type AccountPageProfileConfig = {
  details: AccountPageProfileDetailsConfig
  button: AccountPageProfileButtonConfig
  saved_addresses: AccountPageSavedAddressesConfig
}

// ─── Orders Page ─────────────────────────────────────────────────────────────

export type OrderStatusColors = {
  background_color: string
  text_color: string
}

export type OrderStatuses = {
  delivered: OrderStatusColors
  in_transit: OrderStatusColors
  refunded: OrderStatusColors
  cancelled: OrderStatusColors
}

export type AccountPageOrdersActionBarConfig = {
  header_color: string
  primary_color: string
  background_color: string
  tab_radius: string
}

export type AccountPageOrdersDetailsConfig = {
  group_header_color: string
  order_info_color: string
  background_color: string
  statuses: OrderStatuses
  card_radius: string
  card_border: string
  sides: BorderSides
  button: {
    background_color: string
    text_color: string
  }
  border_color:string
  /** Show the "Cancel Order" option on the orders list and order summary. Defaults to false. */
  show_cancel_option?: boolean
  /** Show the "Track" option on the orders list and order summary. Defaults to false. */
  show_tracking_option?: boolean
}

export type AccountPageOrdersQuickLinksConfig = {
  text_color: string
  socials_color: string
  button: {
    background_color: string
    text_color: string
  }
  statuses: OrderStatuses
}

export type AccountPageOrdersConfig = {
  action_bar: AccountPageOrdersActionBarConfig
  details: AccountPageOrdersDetailsConfig
  quick_links: AccountPageOrdersQuickLinksConfig
}

// ─── Rewards Page ─────────────────────────────────────────────────────────────

export type AccountPageRewardsConfig = {
  balance_card: {
    primary_color: string
    text_color: string
  }
  buttons: {
    primary_color: string
    button_radius: string
    text_color: string
  }
  on_order_placement: {
    background_color: string
    primary_color: string
    text_color: string
    border_color: string
    card_radius: string
    card_border: string
    header_text: string
    sub_text: string
  }
  reward_history: {
    primary_color: string
    text_color: string
  }
}

// ─── Custom Components ───────────────────────────────────────────────────────

/**
 * Raw (snake_case) custom component from SSO ui_attributes - used by both
 * accounts.custom_component_config and the root SSO custom_component_config.
 * On the account page `type` is the page it belongs to: a known page
 * (PROFILE / ORDERS / REWARDS) or any other name, which gets its own page
 * rendering `html_text_string` as its whole UI. The root SSO config only uses `position`.
 */
export type SSOCustomComponent = {
  type?: string
  /** Slot inside a known page. Unused for custom pages, which render the whole HTML. */
  position?: string
  enabled: boolean
  html_text_string: string
}

/** Slots on the Orders page that render `ORDERS` custom components with a matching position */
export type AccountOrdersSlotPosition = 'TOP_SECTION' | 'HEADER_SECTION' | 'BOTTOM_SECTION'

/** A page created from custom components whose type is not a known page */
export type AccountCustomPage = {
  type: string
  label: string
  html: string
}

// ─── Root Config ─────────────────────────────────────────────────────────────

export type AccountPageConfig = {
  layout: AccountPageLayoutConfig
  navigation: AccountPageNavConfig
  pages: {
    profile: AccountPageProfileConfig
    orders: AccountPageOrdersConfig
    rewards: AccountPageRewardsConfig
  }
  custom_component_config?: {
    custom_components: SSOCustomComponent[]
  }
}

// ─── User & Order Data ───────────────────────────────────────────────────────

export type AccountUserPage = {
  isLoading: boolean,
  user: AccountPageUser
}

export type EmailAuthIdentifier = {
  oid: string;
  verified: boolean;
};

export type AccountPageUser = {
  name: string
  phone: string
  primaryEmail?: string
  emailIdentifiers: EmailAuthIdentifier[]
}

export type AccountPageOrderStatus = 'delivered' | 'in_transit' | 'refunded' | 'cancelled'

export type AccountPageOrderItem = {
  id: string
  name: string
  qty: number
  variant: string | null
  price: string
  imageUrl?: string
  productUrl?: string
}

export type OrderTrackingInfo = {
  number: string
  url: string
  company: string
}

export type AccountPageOrder = {
  id: string
  date: string
  orderNumber: string
  status: AccountPageOrderStatus
  totalPrice: string
  cancelled: boolean
  financialStatus: string
  fulfillmentStatus: string
  items: AccountPageOrderItem[]
  /** Flattened tracking entries from the order's fulfillments */
  trackingInfo?: OrderTrackingInfo[]
}

export type OrderAddress = {
  firstName: string
  lastName?: string
  address1: string
  address2?: string
  city: string
  province: string
  country: string
  zip: string
  phone?: string
}

export type OrderTransaction = {
  id: string
  kind: string
  status: string
  gateway: string
  amount: string
  currencyCode: string
}

export type AccountPageOrderDetail = AccountPageOrder & {
  shippingAddress?: OrderAddress
  billingAddress?: OrderAddress
  transactions: OrderTransaction[]
  note?: string | null
}

/** 'no_email' — user has no email at all
 *  'unverified' — user has an email but it's not verified
 *  'verified' — user has a verified primary email, orders can be fetched */
export type AccountOrdersEmailGateState = 'no_email' | 'unverified' | 'verified'

export type AccountOrdersPage = {
  isLoading: boolean
  orders: AccountPageOrder[]
  emailGateState: AccountOrdersEmailGateState
  /** The unverified email to display in the 'unverified' gate state */
  unverifiedEmail?: string
  /** Current sort key ('CREATED_AT' | 'TOTAL_PRICE') */
  sortKey?: string
  /** Whether to sort descending */
  reverse?: boolean
  /** Current search query string */
  searchQuery?: string
  /** Whether more orders can be loaded via cursor pagination */
  hasNextPage?: boolean
  /** Whether a "load more" request is currently in progress */
  isLoadingMoreOrders?: boolean
}

export type AccountPageData = {
  profilePage: AccountUserPage
  ordersPage: AccountOrdersPage
}

// ─── Actions ─────────────────────────────────────────────────────────────────

export type AccountPageActions = {
  /** Tab navigation — required to switch between pages */
  onTabChange: (section: AccountPageExpandedSection) => void
  /** Canvas section click — used by the dashboard editor to highlight panels */
  onSectionClick?: (type: AccountPageExpandedSection, subType?: string) => void
  /** Profile CTA — redirect user to manage their account details */
  onManageDetails?: () => void
  /** Orders — open full order detail view */
  onViewOrder?: (order: AccountPageOrder) => void
  /** Orders — re-add a specific line item to cart */
  onBuyAgain?: (item: AccountPageOrderItem, order: AccountPageOrder) => void
  /** Close / dismiss the account page */
  onClose?: () => void
  /**
   * Open the email management flow.
   * Called by:
   * - Profile page "Manage Email" button (returnToOrders: false)
   * - Orders tab click when no verified email exists (returnToOrders: true)
   */
  onOpenEmailFlow?: (options: { returnToOrders: boolean; initialEmail?: string }) => void

  // ─── Email flow API callbacks ─────────────────────────────────────────────
  /** Send OTP to the given email. Returns the contextId needed for verification. */
  onSendEmailOTP?: (email: string) => Promise<{ contextId: string } | null>
  /** Verify the OTP entered by the user. Throws on failure so the UI can show an error. */
  onVerifyEmailOTP?: (contextId: string, otp: string) => Promise<void>
  /** Set the given email as the primary email. */
  onSetPrimaryEmail?: (email: string) => Promise<void>
  /** Resend the OTP for the given contextId. */
  onResendEmailOTP?: (contextId: string) => Promise<void>
  /** Orders — load the next page of results (cursor pagination) */
  onLoadMoreOrders?: () => void
  /** Orders — change sort order (sortKey: 'CREATED_AT' | 'TOTAL_PRICE') */
  onSortChange?: (sortKey: string, reverse: boolean) => void
  /** Orders — search orders by query string */
  onSearchChange?: (query: string) => void
  /** Orders — fetch full order details (shipping, payment, etc.) */
  onViewOrderDetail?: (numericOrderId: string) => Promise<AccountPageOrderDetail | null>
  /** Orders — cancel an order with a reason note */
  onCancelOrder?: (numericOrderId: string, note: string) => Promise<{ success: boolean; errorMessage?: string | null }>
  /** Custom pages — switch to the page created for this custom component type */
  onPageChange?: (page: string) => void
}

// ─── UI Component Props ───────────────────────────────────────────────────────

export type AccountPageUIProps = {
  uiConfig: AccountPageConfig
  /** A known page, or the type of a custom page */
  activePage: AccountActivePage | string
  data: AccountPageData
  actions: AccountPageActions
  realEstate: AccountPageRealEstate
}
